import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import './Admin.css';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('teacher'); // 'teacher' or 'student'
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    nameJapanese: '',
    specialization: '',
    bio: '',
    nativeLanguage: 'Filipino',
    japaneseLevel: 'N5',
    learningGoals: ''
  });

  useEffect(() => {
    fetchUsers();
  }, [filter]);

  const fetchUsers = async () => {
    try {
      const params = filter !== 'all' ? `?role=${filter}` : '';
      const response = await api.get(`/admin/users${params}`);
      setUsers(response.data.users);
    } catch (error) {
      console.error('ユーザー取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const endpoint = modalType === 'teacher' ? '/admin/users/teacher' : '/admin/users/student';
      await api.post(endpoint, formData);
      setShowModal(false);
      resetForm();
      fetchUsers();
      alert('ユーザーを作成しました');
    } catch (error) {
      alert(error.response?.data?.error || 'エラーが発生しました');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/users/${editingUser.id}`, {
        name: formData.name,
        nameJapanese: formData.nameJapanese,
        nativeLanguage: formData.nativeLanguage,
        japaneseLevel: formData.japaneseLevel,
        role: formData.role
      });
      setShowModal(false);
      setEditingUser(null);
      resetForm();
      fetchUsers();
      alert('ユーザー情報を更新しました');
    } catch (error) {
      alert(error.response?.data?.error || 'エラーが発生しました');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`${userName} を削除してもよろしいですか？\nこの操作は取り消せません。`)) {
      return;
    }
    try {
      await api.delete(`/admin/users/${userId}`);
      fetchUsers();
      alert('ユーザーを削除しました');
    } catch (error) {
      alert(error.response?.data?.error || '削除できませんでした');
    }
  };

  const openCreateModal = (type) => {
    setModalType(type);
    setEditingUser(null);
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      nameJapanese: user.name_japanese || '',
      nativeLanguage: user.native_language || 'Filipino',
      japaneseLevel: user.japanese_level || 'N5',
      role: user.role
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      nameJapanese: '',
      specialization: '',
      bio: '',
      nativeLanguage: 'Filipino',
      japaneseLevel: 'N5',
      learningGoals: ''
    });
  };

  if (loading) {
    return <div className="loading">読み込み中...</div>;
  }

  return (
    <div className="admin-users">
      <div className="page-header">
        <h1>ユーザー管理</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => openCreateModal('teacher')}>
            + 教師を追加
          </button>
          <button className="btn btn-secondary" onClick={() => openCreateModal('student')}>
            + 生徒を追加
          </button>
        </div>
      </div>

      <div className="filter-tabs">
        <button 
          className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          全員
        </button>
        <button 
          className={`filter-tab ${filter === 'student' ? 'active' : ''}`}
          onClick={() => setFilter('student')}
        >
          生徒
        </button>
        <button 
          className={`filter-tab ${filter === 'teacher' ? 'active' : ''}`}
          onClick={() => setFilter('teacher')}
        >
          教師
        </button>
      </div>

      <div className="users-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>名前</th>
              <th>メールアドレス</th>
              <th>役割</th>
              <th>レベル</th>
              <th>レッスン数</th>
              <th>Google連携</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id}>
                <td>
                  <div className="user-cell">
                    {user.profile_picture ? (
                      <img src={user.profile_picture} alt="" className="user-avatar-small" />
                    ) : (
                      <div className="user-avatar-placeholder-small">
                        {user.name?.charAt(0) || '?'}
                      </div>
                    )}
                    <div>
                      <div className="user-name">{user.name}</div>
                      {user.name_japanese && (
                        <div className="user-name-jp">{user.name_japanese}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>
                  <span className={`role-badge role-${user.role}`}>
                    {user.role === 'admin' ? '管理者' : 
                     user.role === 'teacher' ? '教師' : '生徒'}
                  </span>
                </td>
                <td>{user.japanese_level || '-'}</td>
                <td>{user.lessonCount || 0}</td>
                <td>
                  {user.google_id ? (
                    <span className="connected">✓ 連携済み</span>
                  ) : (
                    <span className="not-connected">未連携</span>
                  )}
                </td>
                <td>
                  <div className="action-buttons-small">
                    <button 
                      className="btn-icon" 
                      onClick={() => openEditModal(user)}
                      title="編集"
                    >
                      ✏️
                    </button>
                    {user.role !== 'admin' && (
                      <button 
                        className="btn-icon btn-danger" 
                        onClick={() => handleDeleteUser(user.id, user.name)}
                        title="削除"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ユーザー作成/編集モーダル */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {editingUser 
                  ? 'ユーザー編集' 
                  : modalType === 'teacher' ? '教師を追加' : '生徒を追加'}
              </h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
              <div className="form-group">
                <label>メールアドレス *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData({...formData, email: e.target.value})}
                  required
                  disabled={!!editingUser}
                  placeholder="example@gmail.com"
                />
                {!editingUser && (
                  <small>Googleアカウントのメールアドレスを入力してください</small>
                )}
              </div>
              <div className="form-group">
                <label>名前 *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  required
                  placeholder="山田 太郎"
                />
              </div>
              <div className="form-group">
                <label>名前（日本語）</label>
                <input
                  type="text"
                  value={formData.nameJapanese}
                  onChange={e => setFormData({...formData, nameJapanese: e.target.value})}
                  placeholder="やまだ たろう"
                />
              </div>

              {editingUser && (
                <div className="form-group">
                  <label>役割</label>
                  <select
                    value={formData.role}
                    onChange={e => setFormData({...formData, role: e.target.value})}
                    disabled={editingUser.email === 't-kazaoka@i-nihongo.com'}
                  >
                    <option value="student">生徒</option>
                    <option value="teacher">教師</option>
                  </select>
                </div>
              )}

              {(modalType === 'teacher' && !editingUser) && (
                <>
                  <div className="form-group">
                    <label>専門分野</label>
                    <input
                      type="text"
                      value={formData.specialization}
                      onChange={e => setFormData({...formData, specialization: e.target.value})}
                      placeholder="ビジネス日本語、JLPT対策など"
                    />
                  </div>
                  <div className="form-group">
                    <label>自己紹介</label>
                    <textarea
                      value={formData.bio}
                      onChange={e => setFormData({...formData, bio: e.target.value})}
                      placeholder="教師の経歴や指導方針など"
                      rows={3}
                    />
                  </div>
                </>
              )}

              {(modalType === 'student' || editingUser?.role === 'student') && (
                <>
                  <div className="form-group">
                    <label>母国語</label>
                    <select
                      value={formData.nativeLanguage}
                      onChange={e => setFormData({...formData, nativeLanguage: e.target.value})}
                    >
                      <option value="Filipino">Filipino</option>
                      <option value="English">English</option>
                      <option value="Tagalog">Tagalog</option>
                      <option value="Cebuano">Cebuano</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>日本語レベル</label>
                    <select
                      value={formData.japaneseLevel}
                      onChange={e => setFormData({...formData, japaneseLevel: e.target.value})}
                    >
                      <option value="N5">N5 (初級)</option>
                      <option value="N4">N4</option>
                      <option value="N3">N3 (中級)</option>
                      <option value="N2">N2</option>
                      <option value="N1">N1 (上級)</option>
                    </select>
                  </div>
                </>
              )}

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  キャンセル
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? '更新' : '作成'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
